import { useProcessorStore } from '../stores/processorStore'
import { ChevronDown } from 'lucide-react'

export interface ModelOption {
  id: string
  name: string
  provider: 'grok' | 'gemini' | 'claude'
  description: string
}

export const AVAILABLE_MODELS: ModelOption[] = [
  // Grok models
  {
    id: 'grok-2-vision-1212',
    name: 'Grok 2 Vision',
    provider: 'grok',
    description: 'Best for vision/OCR tasks',
  },
  {
    id: 'grok-4-1-fast-reasoning',
    name: 'Grok 4.1 Fast (Reasoning)',
    provider: 'grok',
    description: 'Fast with reasoning tokens',
  },
  {
    id: 'grok-4-1-fast-non-reasoning',
    name: 'Grok 4.1 Fast (Non-Reasoning)',
    provider: 'grok',
    description: 'Fastest, no thinking tokens',
  },
  // Gemini models
  {
    id: 'gemini-3-pro-preview',
    name: 'Gemini 3 Pro',
    provider: 'gemini',
    description: 'Latest Gemini Pro, most capable',
  },
  {
    id: 'gemini-3-flash-preview',
    name: 'Gemini 3 Flash',
    provider: 'gemini',
    description: 'Fast Gemini 3 model with PDF vision',
  },
  // Claude models
  {
    id: 'claude-sonnet-4-20250514',
    name: 'Claude Sonnet 4',
    provider: 'claude',
    description: 'Best balance of speed/quality',
  },
  {
    id: 'claude-3-5-sonnet-20241022',
    name: 'Claude 3.5 Sonnet',
    provider: 'claude',
    description: 'Excellent structured extraction',
  },
  {
    id: 'claude-3-haiku-20240307',
    name: 'Claude 3 Haiku',
    provider: 'claude',
    description: 'Fastest Claude model',
  },
]

const providerColors = {
  grok: 'bg-purple-600',
  gemini: 'bg-blue-600',
  claude: 'bg-orange-600',
}

const providerLabels = {
  grok: 'Grok',
  gemini: 'Gemini',
  claude: 'Claude',
}

export function ModelSelector() {
  const { selectedModel, setSelectedModel } = useProcessorStore()

  const currentModel = AVAILABLE_MODELS.find((m) => m.id === selectedModel) || AVAILABLE_MODELS[0]

  return (
    <div className="relative">
      <label className="block text-xs text-gray-500 mb-1">Model</label>
      <div className="relative">
        <select
          value={selectedModel}
          onChange={(e) => setSelectedModel(e.target.value)}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 pr-10
                     appearance-none cursor-pointer hover:border-gray-600 focus:outline-none
                     focus:ring-2 focus:ring-blue-500/50 text-sm"
        >
          <optgroup label="Grok (xAI)">
            {AVAILABLE_MODELS.filter((m) => m.provider === 'grok').map((model) => (
              <option key={model.id} value={model.id}>
                {model.name}
              </option>
            ))}
          </optgroup>
          <optgroup label="Gemini (Google)">
            {AVAILABLE_MODELS.filter((m) => m.provider === 'gemini').map((model) => (
              <option key={model.id} value={model.id}>
                {model.name}
              </option>
            ))}
          </optgroup>
          <optgroup label="Claude (Anthropic)">
            {AVAILABLE_MODELS.filter((m) => m.provider === 'claude').map((model) => (
              <option key={model.id} value={model.id}>
                {model.name}
              </option>
            ))}
          </optgroup>
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
      <div className="flex items-center gap-2 mt-1">
        <span
          className={`text-xs px-2 py-0.5 rounded ${providerColors[currentModel.provider]} text-white`}
        >
          {providerLabels[currentModel.provider]}
        </span>
        <span className="text-xs text-gray-500">{currentModel.description}</span>
      </div>
    </div>
  )
}
